/*
  foodora eShop – site chrome behaviour (loaded on every page, defer)
    <fd-nav>           desktop dropdowns: hover, click, keyboard (Esc closes)
    <fd-announcement>  rotates several messages (manual, optional autoplay)
    [data-fd-swap]     closes the current dialog and opens another one
    [data-fd-header]   gets data-scrolled="true" after a few pixels of scroll
  Dialog open/close itself lives in fd-core.js.
*/
(function () {
  'use strict';

  var desktop = window.matchMedia('(min-width: 900px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Focus helper for dialogs that contain a search field ---------- */
  function focusInside(dialog) {
    var target = dialog && dialog.querySelector('[data-fd-autofocus]');
    if (target) window.requestAnimationFrame(function () { target.focus(); });
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-fd-open]');
    if (opener) {
      focusInside(document.querySelector(opener.getAttribute('data-fd-open')));
      return;
    }
    var swap = event.target.closest('[data-fd-swap]');
    if (!swap) return;
    var next = document.querySelector(swap.getAttribute('data-fd-swap'));
    var current = swap.closest('dialog');
    if (!next || typeof next.showModal !== 'function') return;
    event.preventDefault();
    if (current) current.close();
    if (!next.open) next.showModal();
    focusInside(next);
  });

  // A drawer left open while the window grows to desktop would trap focus.
  desktop.addEventListener('change', function (e) {
    var menu = document.getElementById('fd-menu');
    if (e.matches && menu && menu.open) menu.close();
  });

  /* ---------- Header scroll state ---------- */
  var header = document.querySelector('[data-fd-header]');
  if (header) {
    var ticking = false;
    var update = function () {
      ticking = false;
      header.setAttribute('data-scrolled', window.scrollY > 8 ? 'true' : 'false');
    };
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  /* ---------- <fd-nav> ---------- */
  if (!customElements.get('fd-nav')) {
    class FdNav extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        var self = this;
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-fd-nav-item]'));
        this.items.forEach(function (item) { self._bind(item); });
        document.addEventListener('click', function (e) {
          if (!self.contains(e.target)) self.closeAll();
        });
        desktop.addEventListener('change', function () { self.closeAll(); });
      }

      _bind(item) {
        var self = this;
        var toggle = item.querySelector('[data-fd-nav-toggle]');
        var panel = item.querySelector('[data-fd-nav-panel]');
        if (!toggle || !panel) return;

        toggle.addEventListener('click', function () {
          if (self.isOpen(item)) self.close(item); else self.open(item);
        });

        // Hover is an enhancement for mouse users only; touch and keyboard use the button.
        item.addEventListener('pointerenter', function (e) {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(item._timer);
          item._timer = setTimeout(function () { self.open(item); }, 100);
        });
        item.addEventListener('pointerleave', function (e) {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(item._timer);
          item._timer = setTimeout(function () { self.close(item); }, 200);
        });

        item.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.isOpen(item)) {
            e.stopPropagation();
            self.close(item);
            toggle.focus();
          }
        });
        item.addEventListener('focusout', function () {
          setTimeout(function () {
            if (!item.contains(document.activeElement) && !item.matches(':hover')) self.close(item);
          }, 0);
        });
      }

      isOpen(item) {
        var toggle = item.querySelector('[data-fd-nav-toggle]');
        return !!toggle && toggle.getAttribute('aria-expanded') === 'true';
      }

      open(item) {
        if (!desktop.matches) return;
        var self = this;
        this.items.forEach(function (other) { if (other !== item) self.close(other); });
        var toggle = item.querySelector('[data-fd-nav-toggle]');
        var panel = item.querySelector('[data-fd-nav-panel]');
        if (!toggle || !panel) return;
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      }

      close(item) {
        var toggle = item.querySelector('[data-fd-nav-toggle]');
        var panel = item.querySelector('[data-fd-nav-panel]');
        if (!toggle || !panel) return;
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }

      closeAll() {
        var self = this;
        this.items.forEach(function (item) { self.close(item); });
      }
    }
    customElements.define('fd-nav', FdNav);
  }

  /* ---------- <fd-announcement> ---------- */
  if (!customElements.get('fd-announcement')) {
    class FdAnnouncement extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-fd-slide]'));
        if (this.slides.length < 2) return;

        var self = this;
        this.track = this.querySelector('[data-fd-slides]');
        this.prev = this.querySelector('[data-fd-prev]');
        this.next = this.querySelector('[data-fd-next]');
        this.index = 0;
        this.paused = false;
        this.autoplay = this.hasAttribute('data-autoplay') && !reduceMotion.matches;
        this.delay = parseInt(this.getAttribute('data-delay'), 10) || 8000;

        [this.prev, this.next].forEach(function (btn) { if (btn) btn.hidden = false; });
        if (this.prev) this.prev.addEventListener('click', function () { self._manual(-1); });
        if (this.next) this.next.addEventListener('click', function () { self._manual(1); });

        if (this.autoplay) {
          this.addEventListener('mouseenter', function () { self.paused = true; });
          this.addEventListener('mouseleave', function () { self.paused = false; });
          this.addEventListener('focusin', function () { self.paused = true; });
          this.addEventListener('focusout', function () { self.paused = false; });
          this._start();
        }
      }

      disconnectedCallback() { this._stop(); }

      _show(i) {
        var n = this.slides.length;
        this.index = (i + n) % n;
        var active = this.index;
        this.slides.forEach(function (slide, k) { slide.classList.toggle('is-active', k === active); });
      }

      // Any manual navigation ends autoplay and switches to polite announcements.
      _manual(step) {
        this._stop();
        this.autoplay = false;
        if (this.track) this.track.setAttribute('aria-live', 'polite');
        this._show(this.index + step);
      }

      _start() {
        var self = this;
        this._timer = setInterval(function () {
          if (!self.paused && !document.hidden) self._show(self.index + 1);
        }, this.delay);
      }

      _stop() {
        if (this._timer) clearInterval(this._timer);
        this._timer = null;
      }
    }
    customElements.define('fd-announcement', FdAnnouncement);
  }
})();
